import Cookies from 'js-cookie'
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, generateId, lastAssistantMessageIsCompleteWithToolCalls, type PrepareSendMessagesRequest, type UIMessage } from 'ai';
import { baseURL, PAYMENT_REQUIRED_EVENT } from '@/lib/axios'

interface UseAssistantChatProps {
  conversationId: string
  foldersId?: string[]
  assistantId?: string
  versionId?: string
  isEmbed?: boolean
  name?: string
  description?: string
  tools?: unknown
  knowledge_base?: Array<{
    folder_id: string
    usage_instructions: string
  }>
}

interface RequestBody {
  message: unknown
  id: string
  assistant: boolean
  foldersId?: string[]
  assistantId?: string
  previewAssistantData?: {
    name: string
    description: string
    version_id?: string
    tools?: unknown
    knowledge_base?: Array<{
      folder_id: string
      usage_instructions: string
    }>
  }
}

export const useAssistantChat = ({
  conversationId,
  foldersId = [],
  assistantId,
  isEmbed = false,
  name = 'Alan',
  description = 'Alan is a helpful assistant that can answer questions and help with tasks.',
  versionId,
  tools,
  knowledge_base,
}: UseAssistantChatProps) => {
  const [input, setInput] = useState('');

  const propsRef = useRef({
    assistantId,
    versionId,
    isEmbed,
    foldersId,
    name,
    description,
    tools,
    knowledge_base,
  })

  useEffect(() => {
    propsRef.current = {
      assistantId,
      versionId,
      isEmbed,
      foldersId,
      name,
      description,
      tools,
      knowledge_base,
    }
  }, [assistantId, versionId, isEmbed, foldersId, name, description, tools, knowledge_base])

  const handlePaymentRequired = useCallback(() => {
    const paymentRequiredEvent = new CustomEvent(PAYMENT_REQUIRED_EVENT)
    window.dispatchEvent(paymentRequiredEvent)
  }, [])

  const transport = useMemo(() => {
    const prepareRequest: PrepareSendMessagesRequest<UIMessage> = ({ messages, id }) => {
      const props = propsRef.current
      
      const requestBody: RequestBody = {
        message: messages[messages.length - 1],
        id,
        assistant: true,
        foldersId: props.foldersId,
        assistantId: props.assistantId,
      }

      if (!props.isEmbed) {
        requestBody.previewAssistantData = {
          name: props.name,
          description: props.description,
          version_id: props.versionId,
          tools: props.tools,
          knowledge_base: props.knowledge_base,
        }
      }

      return {
        body: requestBody,
      }
    };

    return new DefaultChatTransport({
      api: baseURL,
      headers: () => ({
        'Content-Type': 'application/json',
        Authorization: `${Cookies.get('workstation_access_token') ?? ''}`,
      }),
      fetch: async (_url: RequestInfo | URL, options?: RequestInit) => {
        const props = propsRef.current
        
        if (!props.assistantId) {
          throw new Error('Assistant ID is not available yet')
        }
        
        const dynamicUrl = props.isEmbed && props.assistantId
          ? `${baseURL}/ai-hub/assistant/${props.assistantId}/ask`
          : `${baseURL}/ai-hub/assistant/${props.assistantId}/preview`
        
        const response = await fetch(dynamicUrl, options)
        if (response.status === 402) {
          handlePaymentRequired()
        }
        return response
      },
      prepareSendMessagesRequest: prepareRequest,
    })
  }, [handlePaymentRequired])

  const { messages, sendMessage, stop, status } = useChat<UIMessage>({
    id: conversationId,
    transport,
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    onError: (error) => {
      try {
        const payload = JSON.parse(error.message)
        if (payload?.errorCode === '402') {
          handlePaymentRequired()
        }
      } catch {
        // ignore
      }
    },
  })

  const isLoading = status === 'submitted' || status === 'streaming'

  const handleSubmit = useCallback((e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault()
    const trimmed = input.trim()
    if (!trimmed) return

    const userMessage: UIMessage = {
      id: generateId(),
      role: 'user',
      parts: [{ type: 'text', text: trimmed }],
    }

    void sendMessage(userMessage)
    setInput('')
  }, [input, sendMessage])

  const handleNewChat = () => {
    setInput('')
    stop()
  }

  return {
    messages,
    handleSubmit,
    input,
    setInput,
    isLoading,
    stop,
    handleNewChat,
  }
}
